import { Controller } from "@hotwired/stimulus";

// Client-side validation + submission for the shared Connect lead-capture form
// (app/views/connect/shared/_lead_form.html.erb).
//
// Validates, then POSTs to the capture endpoint (POST /_lead,
// Connect::LeadsController) via fetch() with Accept: application/json and shows
// an inline thank-you on success (SUR-1865). The form also has a real
// action/method, so with JS absent it does a normal POST and the server
// redirects back with ?rsvp=success — progressive enhancement, never required.
export default class extends Controller {
  static targets = [
    "form", "name", "email", "phone", "error", "success", "successPrivate",
    "redirectLink", "rating", "comment", "reviewCopy", "reviewText", "copyButton",
  ];
  static values = {
    pageType: String,
    endpoint: { type: String, default: "/_lead" },
    // Review flow (SUR-1870): where to send the visitor after capture (the agent's
    // Google reviews URL). Empty for non-review pages.
    redirectUrl: { type: String, default: "" },
  };

  async submit(event) {
    event.preventDefault();
    this.clearError();

    const email = this.hasEmailTarget ? this.emailTarget.value.trim() : "";
    if (!this.isValidEmail(email)) {
      this.showError("Please enter a valid email address.");
      if (this.hasEmailTarget) this.emailTarget.focus();
      return;
    }
    const phone = this.hasPhoneTarget ? this.phoneTarget.value.trim() : "";
    if (!phone) {
      this.showError("Please enter your mobile phone number.");
      if (this.hasPhoneTarget) this.phoneTarget.focus();
      return;
    }
    // Mirror the server's minimum (10 digits with area code) so a typo'd phone
    // is caught before the round-trip.
    if (phone.replace(/\D/g, "").length < 10) {
      this.showError("Please enter a valid mobile phone number with area code.");
      if (this.hasPhoneTarget) this.phoneTarget.focus();
      return;
    }

    // Review page (SUR-1870): a star rating is required before we capture.
    if (this.hasRatingTarget && !this.selectedRating()) {
      this.showError("Please select a star rating.");
      return;
    }

    this.setSubmitting(true);
    try {
      const response = await fetch(this.endpointValue, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(this.formTarget),
        // same-origin (NOT omit): the POST must carry cookies so it reaches the
        // app on gated environments (the staging HTTP Basic Auth middleware
        // requires the signed `staging_auth` cookie on every non-excluded path,
        // and /_lead is not excluded — omitting cookies there returns a
        // non-OK response and the AJAX path silently falls back to a full-page
        // reload). It also lets a signed-in registrant submit without re-typing
        // contact info (Connect::LeadsController reads connect_current_person
        // from the session cookie — SUR-2302).
        credentials: "same-origin",
      });

      if (!response.ok) throw new Error(`Lead capture failed (${response.status})`);
      const data = await response.json().catch(() => ({}));
      this.showSuccess(data);
    } catch (_e) {
      // Fall back to a native form submit so the visitor's RSVP still lands
      // (server returns the ?rsvp=success redirect for the HTML branch).
      this.setSubmitting(false);
      this.formTarget.submit();
    }
  }

  isValidEmail(value) {
    // Pragmatic check — the server does authoritative validation.
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  setSubmitting(isSubmitting) {
    const button = this.formTarget.querySelector("button[type=submit]");
    if (button) button.disabled = isSubmitting;
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.classList.add("is-visible");
  }

  clearError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = "";
    this.errorTarget.classList.remove("is-visible");
  }

  // The selected star rating (1–5) or null. Reads the checked radio so it works
  // regardless of how the widget is styled.
  selectedRating() {
    const checked = this.element.querySelector('input[name="rating"]:checked');
    return checked ? checked.value : null;
  }

  // `data` is the JSON capture response. forward_to_google gates the Google CTA
  // (true → show the CTA + copy-your-review helper). high_rating gates the
  // thank-you copy: only a genuinely low rating (high_rating === false) sees the
  // private thank-you — a high rating with no Google link still gets the upbeat
  // one (just no CTA), never the private/low message.
  showSuccess(data = {}) {
    // Read the written review BEFORE hiding the form.
    const comment = this.hasCommentTarget ? this.commentTarget.value.trim() : "";
    if (this.hasFormTarget) this.formTarget.hidden = true;

    const forward = Boolean(data && data.forward_to_google);

    // Low-rating review path: private thank-you, no Google handoff. Gate on the
    // rating being low — NOT on `forward` — so a high rating with no Google link
    // doesn't fall through to the "we'll follow up personally" message.
    if (this.hasRatingTarget && data.high_rating === false && this.hasSuccessPrivateTarget) {
      this.successPrivateTarget.hidden = false;
      return;
    }

    if (this.hasSuccessTarget) this.successTarget.hidden = false;

    // High rating: DON'T auto-open Google. Google's "write a review" screen can't
    // be pre-filled from the URL, so we surface what the visitor wrote with a Copy
    // button; they copy it, then click the (real-anchor) button to open Google and
    // paste. The click is a genuine user gesture, so the new tab opens reliably.
    if (forward && comment && this.hasReviewTextTarget && this.hasReviewCopyTarget) {
      this._reviewComment = comment;
      this.reviewTextTarget.textContent = comment;
      this.reviewCopyTarget.hidden = false;
    }
    if (forward && this.hasRedirectLinkTarget) this.redirectLinkTarget.focus();
  }

  // Copy the visitor's written review to the clipboard so they can paste it into
  // Google. Falls back to execCommand for non-secure contexts.
  async copyReview() {
    const text = this._reviewComment ||
      (this.hasReviewTextTarget ? this.reviewTextTarget.textContent : "");
    if (!text) return;

    const flash = () => {
      if (!this.hasCopyButtonTarget) return;
      const original = this.copyButtonTarget.dataset.label || this.copyButtonTarget.textContent;
      this.copyButtonTarget.dataset.label = original;
      this.copyButtonTarget.textContent = "Copied!";
      setTimeout(() => {
        if (this.hasCopyButtonTarget) this.copyButtonTarget.textContent = original;
      }, 2000);
    };

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        this.fallbackCopy(text);
      }
      flash();
    } catch (_e) {
      this.fallbackCopy(text);
      flash();
    }
  }

  fallbackCopy(text) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); } catch (_e) { /* best effort */ }
    document.body.removeChild(ta);
  }
}
